import React, { useState, useEffect } from 'react';
import { ConfirmacaoHost } from './components/confirmacao';
import { createBrowserRouter, RouterProvider, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import apiService from './services/apiService';
import { getStoreName, extractSubdomain, isQaEnvironment } from './utils/subdomain';

// Components
import Header from './components/Header';
import Sidebar from './components/Sidebar';
import Loading from './components/Loading';
import CodespacesStoreSelector from './components/CodespacesStoreSelector';

// Pages
import Login from './pages/Login';
import HomeAdmin from './pages/HomeAdmin';
import Settings from './pages/Settings';
import Recordings from './pages/Recordings';
import UserRegister from './pages/UserRegister';
import UserCreate from './pages/UserCreate';
import UserCreateAdmin from './pages/UserCreateAdmin';
import StoreCreate from './pages/StoreCreate';
import StoreEdit from './pages/StoreEdit';
import UnitCreate from './pages/UnitCreate';
import UnitEdit from './pages/UnitEdit';
import CompanyCreate from './pages/CompanyCreate';
import CompanyEdit from './pages/CompanyEdit';
import UserEdit from './pages/UserEdit';
import UserEditAdmin from './pages/UserEditAdmin';
import FeedbacksAdmin from './pages/FeedbacksAdmin';
import FeedbacksSearch from './pages/FeedbacksSearch';
import FeedbacksManager from './pages/FeedbacksManager';
import SupportChat from './pages/SupportChat';
import SupportChatGeneric from './pages/SupportChatGeneric';
import AgentDashboard from './pages/AgentDashboard';
import PromptEditor from './pages/PromptEditor';
import AnalyticDashboard from './pages/AnalyticDashboard';
import AnalyticDashboardIndex from './pages/AnalyticDashboardIndex';
import AwonMonitor from './pages/AwonMonitor';
import WhitelabelConfig from './pages/WhitelabelConfig';
import RevisaoPontos from './pages/RevisaoPontos';
import Reclamacoes from './pages/Reclamacoes';
import ReclamacaoRevisao from './pages/ReclamacaoRevisao';
import PromocoesWhitelabel from './pages/PromocoesWhitelabel';
import PromocaoRevisao from './pages/PromocaoRevisao';
import PromocaoLote from './pages/PromocaoLote';
import MetasConfig from './pages/MetasConfig';
import KeytermsConfig from './pages/KeytermsConfig';
import Comunicados from './pages/Comunicados';
import Regras from './pages/Regras';
import Termos from './pages/Termos';
import EndpointsPublicados from './pages/EndpointsPublicados';
import McpPermissoes from './pages/McpPermissoes';
import WhatsappRelatorios from './pages/WhatsappRelatorios';
import Apps from './pages/Apps';
import McpAuditoria from './pages/McpAuditoria';
import AppVersions from './pages/AppVersions';
import Cenas from './pages/Cenas';
import SyncAmbientes from './pages/SyncAmbientes';
import SyncDiff from './pages/SyncDiff';
import SyncSelecao from './pages/SyncSelecao';
import SyncPlano from './pages/SyncPlano';
import CustosAtendimento from './pages/CustosAtendimento';
import Programas from './pages/Programas';
import GruposDeTurmas from './pages/GruposDeTurmas';
import Configuracoes from './pages/Configuracoes';
import Escalas from './pages/Escalas';
import Turmas from './pages/Turmas';
import TurmaDetalhe from './pages/TurmaDetalhe';

// Styles
import './styles/global.css';
import './App.css';

// ─────────────────────────────────────────────────────────────────────
// Subdomains que exibem apenas a mensagem "Dashboard em análise..."
// (sem header, sidebar nem rotas). Para reativar o painel normal de
// um cliente, basta REMOVER o subdomain desta lista.
// ─────────────────────────────────────────────────────────────────────
const DASHBOARD_UNDER_REVIEW_SUBDOMAINS = [];

const QABadge = () => {
  if (!isQaEnvironment()) return null;
  return (
    <div style={{
      position: 'fixed',
      bottom: '24px',
      left: '24px',
      zIndex: 9999,
      backgroundColor: '#f59e0b',
      color: '#1a1a1a',
      fontWeight: '800',
      fontSize: '1.1rem',
      letterSpacing: '0.08em',
      padding: '14px 24px',
      borderRadius: '12px',
      boxShadow: '0 4px 20px rgba(0,0,0,0.35)',
      userSelect: 'none',
      pointerEvents: 'none',
      lineHeight: 1,
    }}>
      ⚠ AMBIENTE QA
    </div>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  useEffect(() => {
    checkAuthentication();
  }, []);

  const checkAuthentication = async () => {
    try {
      const result = await apiService.checkAuth();
      setIsAuthenticated(result.authenticated);
    } catch (error) {
      console.error('Erro na verificação de autenticação:', error);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  }; if (loading) {
    return <Loading message="Verificando autenticação..." fullScreen />;
  }

  if (!isAuthenticated) {
    // Preserva a URL pedida para o login devolver a pessoa nela, em QUALQUER domínio.
    // Era restrito ao portal admin, e o efeito era o link direto de um dashboard
    // (leroymerlin.alia.do/analytic-dashboard/<slug>) parar na home depois de autenticar —
    // justamente o caso em que alguém compartilhou o link para abrir algo específico.
    // Quem valida o destino é o `destinoSeguro` do Login: aqui só se registra a intenção.
    const currentPath = location.pathname + location.search;
    const loginUrl = currentPath && currentPath !== '/'
      ? `/login?redirect=${encodeURIComponent(currentPath)}`
      : '/login';
    return <Navigate to={loginUrl} replace />;
  }

  return children;
};

// Main App Layout Component
const AppLayout = () => {
  const [user, setUser] = useState(null);
  const [storeName, setStoreName] = useState('Aliado');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  useEffect(() => {
    loadUserData();
    loadStoreName();
  }, []);

  const loadUserData = async () => {
    try {
      const userData = await apiService.getUserInfo();
      setUser(userData.user);
    } catch (error) {
      console.error('Erro ao carregar dados do usuário:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStoreName = async () => {
    try {
      const stores = await apiService.getStores();
      const name = getStoreName(stores);
      setStoreName(name);
    } catch (error) {
      console.error('Erro ao carregar nome da loja:', error);
    }
  };

  const handleLogout = async () => {
    try {
      await apiService.logout();
      window.location.href = '/login';
    } catch (error) {
      console.error('Erro ao fazer logout:', error);
      // Forçar logout local mesmo com erro
      window.location.href = '/login';
    }
  };

  const handleNavigation = (path) => {
    window.location.href = path;
  };

  if (loading) {
    return <Loading message="Carregando aplicação..." fullScreen />;
  }

  // Bloqueio de dashboard por subdomain (ver lista no topo do arquivo).
  // Usuários com role = 'admin' passam direto e veem o painel normal.
  const currentSubdomain = extractSubdomain();

  // Magalu: exibe SOMENTE o dashboard estático em tela cheia, sem Home/menu/Sidebar.
  // O acesso já está protegido pelo login do subdomínio (ProtectedRoute) e o
  // /staticdash da API valida a role. Vale tanto para magalu.alia.do quanto magalu-qa.alia.do.
  if (currentSubdomain === 'magalu') {
    return (
      <div className="magalu-fullscreen-dash">
        <AnalyticDashboard user={user} />
      </div>
    );
  }

  if (DASHBOARD_UNDER_REVIEW_SUBDOMAINS.includes(currentSubdomain) && user?.role !== 'admin') {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: 'system-ui, -apple-system, sans-serif',
          fontSize: '1.5rem',
          color: '#1a1a2e',
          background: '#f8f9fa',
          padding: '2rem',
          textAlign: 'center',
        }}
      >
        Dashboard em análise...
      </div>
    );
  }

  return (
    <div className="app">
      <Header
        storeName={storeName}
        user={user}
        onLogout={handleLogout}
        onMenuClick={() => setSidebarOpen(true)}
      />

      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        currentPage={location.pathname}
        onNavigate={handleNavigation}
        user={user}
        subdomain={extractSubdomain()}
      />

      <main className="app-main">
        <Routes>
          {/* A raiz do portal do CLIENTE é o Dash Analítico, não mais o Dash Monitor
              (pages/Home.js). O Monitor foi escondido de todos os tenants a pedido
              (02/09/2026): a rota some junto com o item do menu, senão ela continuaria
              aberta por URL. O arquivo fica no repo — é só a porta que fechou.
              `admin.alia.do` não muda: continua na HomeAdmin. */}
          <Route path="/" element={
            extractSubdomain() === 'admin'
              ? <HomeAdmin />
              : <Navigate to="/analytic-dashboard" replace />
          } />
          <Route path="/settings" element={<Settings />} />
          <Route path="/recordings" element={<Recordings />} />
          <Route path="/users" element={<UserRegister />} />
          <Route path="/users/create" element={<UserCreate />} />
          <Route path="/users/create-admin" element={<UserCreateAdmin />} />
          <Route path="/users/edit" element={<UserEdit />} />
          <Route path="/users/edit-admin" element={<UserEditAdmin />} />
          <Route path="/stores/create" element={<StoreCreate />} />
          <Route path="/stores/edit" element={<StoreEdit />} />
          <Route path="/units/create" element={<UnitCreate />} />
          <Route path="/units/edit" element={<UnitEdit />} />
          <Route path="/companies/create" element={<CompanyCreate />} />
          <Route path="/companies/edit" element={<CompanyEdit />} />
          <Route path="/feedbacks" element={<FeedbacksAdmin />} />
          <Route path="/feedbacks-manager" element={<FeedbacksManager />} />
          <Route path="/support" element={<SupportChat />} />
          <Route path="/support-generic" element={<SupportChatGeneric />} />
          <Route path="/agent-dashboard" element={<AgentDashboard />} />
          {/* Rota antiga mantida como caminho direto; a tela vive na aba /config/prompts. Ganhou a
              guarda de admin que faltava — era a única dessas telas sem guarda no front (§9.5). */}
          <Route path="/prompt-editor" element={
            user?.role === 'admin' ? <PromptEditor /> : <Navigate to="/" replace />
          } />
          {/* Índice do Dash Analítico — a home do portal do cliente. Ele mesmo decide o
              que mostrar: abre direto quando só há um dashboard, e mostra "Nenhum
              dashboard disponível" quando não há nenhum publicado. */}
          <Route path="/analytic-dashboard" element={<AnalyticDashboardIndex />} />
          <Route path="/analytic-dashboard/:slug" element={<AnalyticDashboard user={user} />} />
          {/* O endereço curto. `/dash` é fixo; o que vem depois é o `url_path` que
              quem publica escolheu, ou o slug quando não escolheu nenhum. */}
          <Route path="/dash/:ref" element={<AnalyticDashboard user={user} />} />
          <Route path="/awon-monitor" element={
            user?.role === 'admin' ? <AwonMonitor /> : <Navigate to="/" replace />
          } />
          <Route path="/whitelabel-config" element={
            user?.role === 'admin' ? <WhitelabelConfig user={user} /> : <Navigate to="/" replace />
          } />
          <Route path="/revisao-pontos" element={
            user?.role === 'admin' ? <RevisaoPontos user={user} /> : <Navigate to="/" replace />
          } />
          <Route path="/reclamacoes-pontos" element={
            user?.role === 'admin' ? <Reclamacoes user={user} /> : <Navigate to="/" replace />
          } />
          <Route path="/reclamacoes-pontos/:subdomain/:transcriptionId/:step" element={
            user?.role === 'admin' ? <ReclamacaoRevisao user={user} /> : <Navigate to="/" replace />
          } />
          {/* Promoções de whitelabel: a trava de verdade é o requireInterno do BFF e, para decidir,
              o JWT de quem decide + o tema do MCP, conferidos pela API. */}
          <Route path="/promocoes-whitelabel" element={
            user?.role === 'admin' ? <PromocoesWhitelabel user={user} /> : <Navigate to="/" replace />
          } />
          <Route path="/promocoes-whitelabel-lote/:batchId" element={
            user?.role === 'admin' ? <PromocaoLote user={user} /> : <Navigate to="/" replace />
          } />
          <Route path="/promocoes-whitelabel/:id" element={<Navigate to="itens" replace />} />
          <Route path="/promocoes-whitelabel/:id/:step" element={
            user?.role === 'admin' ? <PromocaoRevisao user={user} /> : <Navigate to="/" replace />
          } />
          <Route path="/metas-config" element={
            user?.role === 'admin' ? <MetasConfig user={user} /> : <Navigate to="/" replace />
          } />
          <Route path="/keyterms-config" element={
            user?.role === 'admin' ? <KeytermsConfig user={user} /> : <Navigate to="/" replace />
          } />
          <Route path="/config" element={
            user?.role === 'admin' ? <Configuracoes user={user} /> : <Navigate to="/" replace />
          } />
          <Route path="/config/:aba" element={
            user?.role === 'admin' ? <Configuracoes user={user} /> : <Navigate to="/" replace />
          } />
          {/* Escalas de trabalho. A guarda que VALE é a das rotas do BFF (requireAuth + requireAdmin +
              user_id_admin da sessão) e a da API (role=admin); esta só evita abrir a tela vazia. */}
          <Route path="/escalas" element={
            user?.role === 'admin' ? <Escalas user={user} /> : <Navigate to="/" replace />
          } />
          <Route path="/escalas/:aba" element={
            user?.role === 'admin' ? <Escalas user={user} /> : <Navigate to="/" replace />
          } />
          {/* Sincronizador de Ambientes — só em QA e só para admin irrestrito da Aliado
              Admin. O `can_sync` vem do /auth/me; a trava de ambiente e a de verdade
              ficam na API (sync_routes._require_sync_admin + APP_ENV). */}
          <Route path="/sync" element={
            user?.role === 'admin' ? <SyncAmbientes user={user} /> : <Navigate to="/" replace />
          } />
          <Route path="/sync/plano/:id" element={
            user?.role === 'admin' ? <SyncPlano user={user} /> : <Navigate to="/" replace />
          } />
          <Route path="/sync/:direcao/:tenant/:entidade" element={
            user?.role === 'admin' ? <SyncSelecao user={user} /> : <Navigate to="/" replace />
          } />
          <Route path="/sync/:direcao/:tenant" element={
            user?.role === 'admin' ? <SyncDiff user={user} /> : <Navigate to="/" replace />
          } />
          <Route path="/turmas" element={
            user?.role === 'admin' ? <Turmas user={user} /> : <Navigate to="/" replace />
          } />
          <Route path="/turmas/:id" element={
            user?.role === 'admin' ? <TurmaDetalhe user={user} /> : <Navigate to="/" replace />
          } />
          <Route path="/programas" element={
            user?.role === 'admin' ? <Programas user={user} /> : <Navigate to="/" replace />
          } />
          <Route path="/grupos-de-turmas" element={
            user?.role === 'admin' ? <GruposDeTurmas user={user} /> : <Navigate to="/" replace />
          } />
          <Route path="/comunicados" element={
            user?.role === 'admin' ? <Comunicados user={user} /> : <Navigate to="/" replace />
          } />
          {/* Regras e Conquistas — a trava de verdade é o `requireInterno` das rotas do BFF
              (routes/api.js): papel 'admin' inclui conta de CLIENTE, e uma regra ligada age sobre
              vendedores. Isto aqui só evita oferecer uma tela que a API negaria. */}
          <Route path="/regras" element={
            user?.role === 'admin' ? <Regras user={user} /> : <Navigate to="/" replace />
          } />
          <Route path="/termos" element={
            user?.role === 'admin' ? <Termos user={user} /> : <Navigate to="/" replace />
          } />
          <Route path="/endpoints-publicados" element={
            user?.role === 'admin' ? <EndpointsPublicados user={user} /> : <Navigate to="/" replace />
          } />
          {/* MCP — telas internas. A guarda que VALE é o requireInterno das rotas do BFF
              (routes/api.js): role='admin' inclui contas de CLIENTE, e estas telas mostram
              SQL que rodou contra produção e quem pode escrever lá. O teste aqui é só para
              a navegação não oferecer uma página que a API vai recusar. */}
          {/* A trava de verdade é o `requireInterno` da rota do Express: papel 'admin'
              inclui conta de CLIENTE. Isto aqui só evita mostrar uma tela que a API negaria. */}
          <Route path="/apps" element={
            user?.role === 'admin' ? <Apps user={user} /> : <Navigate to="/" replace />
          } />
          <Route path="/whatsapp/relatorios" element={
            user?.role === 'admin' ? <WhatsappRelatorios user={user} /> : <Navigate to="/" replace />
          } />
          <Route path="/mcp/permissoes" element={
            user?.role === 'admin' ? <McpPermissoes user={user} /> : <Navigate to="/" replace />
          } />
          <Route path="/mcp/auditoria" element={
            user?.role === 'admin' ? <McpAuditoria user={user} /> : <Navigate to="/" replace />
          } />
          <Route path="/feedbacks-search" element={
            user?.role === 'admin' ? <FeedbacksSearch user={user} /> : <Navigate to="/" replace />
          } />
          <Route path="/app-versions" element={
            user?.role === 'admin' ? <AppVersions user={user} /> : <Navigate to="/" replace />
          } />
          <Route path="/cenas" element={
            user?.role === 'admin' ? <Cenas user={user} /> : <Navigate to="/" replace />
          } />
          <Route path="/custos-atendimento" element={
            user?.role === 'admin' ? <CustosAtendimento /> : <Navigate to="/" replace />
          } />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>
    </div>
  );
};

// Main App Component
// Data router com uma rota "*" envolvendo as <Routes> de sempre: as rotas não mudam, mas
// useBlocker passa a funcionar (a revisão do VAR pede confirmação ao sair com rascunho).
function Shell() {
  return (
      <div className="App">
        <ConfirmacaoHost />
        <QABadge />
        <CodespacesStoreSelector />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route
            path="/*"
            element={
              <ProtectedRoute>
                <AppLayout />
              </ProtectedRoute>
            }
          />
        </Routes>
      </div>
  );
}

const router = createBrowserRouter([{ path: '*', element: <Shell /> }]);

function App() {
  return <RouterProvider router={router} />;
}

export default App;
